/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { useEffect } from 'react';
import { useDispatch } from 'redux-react-hook';

import { FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { daysFromNow } from '@zapier/date';
import { Colors, Link as DesignSystemLink } from '@zapier/design-system';

import {
  BannerWithModal as CompaniesBannerWithModal,
  TrialEndedModal as CompaniesTrialEndedModal,
  TrialWelcomeModal as CompaniesTrialWelcomeModal,
} from '@zapier/design-system-company';

import {
  AnnualBillingNudge,
  buildUpgradeUrl,
  isOnUpgradeRoute,
  ProfessionalTrialEndedBanner,
  TeamsDowngradedBanner,
  useAnnualBillingNudge,
  useShouldRenderProfessionalTrialEndedBanner,
  useShouldRenderTeamsDowngradedBanner,
} from '@zapier/design-system-revenue';

import {
  ReducedTaskTeamCompanyTopBarBanner,
  tracking,
  useReducedTaskTeamCompanyTopBarBanner,
} from '@zapier/promotions';

import {
  Account,
  isCurrentProfileAdminOfCurrentAccount,
  Profile,
  Plan,
} from '@zapier/shared-entities';

import { useRedux } from '@zapier/toolbox-redux-framework';

import { isUrlBlacklisted } from '@zapier/url-utils';

import Button from 'app/common/components/Button';
import TeamsPricing from 'app/team/components/TeamsPricing';

import {
  COMPANY_TRIAL_ENDED_MODAL_TRACKING_KEY,
  COMPANY_TRIAL_WELCOME_MODAL_TRACKING_KEY,
} from 'app/company-account/utils/constants';

import { isVBOrLegacyUrl } from 'app/developer-v3/utils';
import { isRefreshRequired } from 'app/redux-boneyard/refresh/Selectors';
import { isOnSharedZapPOC, isOnSharedZapRoute } from 'app/shared-zap/utils';

import { DismissibleJobsCta } from './DismissibleJobsCta';
import { JobsCta } from './JobsCta';
import { ProfessionalTrialBanner } from './ProfessionalTrialBanner';
import { ToggleTeamsUpgradeButton } from './ToggleTeamsUpgradeButton';
import { useSize } from './useSize';

type RootStyleProps = {
  backgroundColor?: string;
  fontColor?: string;
};

const rootStyles = ({ backgroundColor, fontColor }: RootStyleProps = {}) => css`
  background-color: ${backgroundColor || Colors.aquamarine100};
  color: ${fontColor || Colors.grey900};
  font-size: 13px;
  line-height: 1.4;
  padding: 5px 20px;
  text-align: center;
  width: 100%;

  &:empty {
    display: none;
  }
`;

// Should probably move this to routes, but there may be server-only routes here.
const hiddenRouteMatches = [
  '/engine/auth',
  '/sign-up',
  '/app/login',
  '/app/editor',
  '/app/onboarding',
  '/app/instant',
  '/app/settings',
];

type TopBarComponentProps = {
  backgroundColor?: string;
  fontColor?: string;
  render: () => React.ReactNode;
  shouldRender: boolean;
};

const TopBar = () => {
  const { companyPlan, currentAccount, currentProfile, state } = useRedux(
    s => ({
      companyPlan: Plan.selectors.all.findCompanyPlan(s),
      currentAccount: Account.selectors.all.currentAccount(s),
      currentProfile: Profile.selectors.all.currentProfile(s),
      state: s,
    })
  );

  const dispatch = useDispatch();
  const size = useSize();

  useEffect(() => {
    dispatch(Account.actions.loadCollection());
    dispatch(Profile.actions.loadCollection());
    dispatch(Plan.actions.loadCollection());
  }, [dispatch]);

  const currentPlan = currentAccount?.data?.plan;
  const currentAccountId = currentAccount?.data?.id;
  const currentProfileId = currentProfile?.data?.id;

  const isLoggedOut = !currentPlan;

  const teamColors = {
    backgroundColor: Colors.purple500,
    fontColor: Colors.white,
  };

  const companyColors = {
    backgroundColor: Colors.grey900,
    fontColor: Colors.white,
  };

  // These are in order of precedence. The front (top) of the list will be processed first.
  const topBarComponents: TopBarComponentProps[] = [
    {
      /**
       * Zapier has been updated and requires a page refresh
       */
      render: () => (
        <div>
          Hi! We just made some upgrades to Zapier for you.{' '}
          <Button kind="as-link" onClick={() => window.location.reload()}>
            Refresh now
          </Button>{' '}
          to get the new version.
        </div>
      ),

      shouldRender: isRefreshRequired(state),
    },
    {
      /**
       * User has a banner message
       */
      render: () => (
        <div
          dangerouslySetInnerHTML={{
            __html: Profile.selectors.bannerMessage(currentProfile),
          }}
        />
      ),

      shouldRender: Profile.selectors.bannerMessage(currentProfile),
    },
    {
      /**
       * Account is set to unlimited in admin
       *
       * This config mostly applies to admins, but not exclusively. Any configs
       * below this one effectively don't apply if the account `isUnlimited`
       * since this one will "render" first.
       */
      render: () => null,
      shouldRender: Account.selectors.isUnlimited(currentAccount),
    },
    {
      backgroundColor: teamColors.backgroundColor,
      fontColor: teamColors.fontColor,

      /**
       * Teams trial is active
       */
      render: () => {
        const shouldRenderButton = isCurrentProfileAdminOfCurrentAccount(state);
        const shouldAutoUpgradeToTeams = Account.selectors.shouldAutoUpgradeToTeams(
          currentAccount
        );

        const onToggleTeamsAutoUpgrade = (id: number) => {
          const messages = {
            error: 'Could not toggle teams auto-upgrade',
          };

          dispatch(
            Account.actions.toggleTeamsAutoUpgrade(
              !shouldAutoUpgradeToTeams,
              id
            )
          );

          dispatch(Account.actions.saveEntity(messages, id));
        };

        return (
          <ToggleTeamsUpgradeButton
            id={currentAccountId}
            onToggleTeamsAutoUpgrade={onToggleTeamsAutoUpgrade}
            shouldAutoUpgradeToTeams={shouldAutoUpgradeToTeams}
            shouldRenderButton={shouldRenderButton}
            size={size}
          >
            <span>
              You’re on a free trial of Zapier for Teams and have{' '}
              {Account.selectors.teamsTrialDaysLeft(currentAccount)} days left
              in your trial. After that you will{' '}
              {shouldAutoUpgradeToTeams ? (
                <span>
                  be automatically upgraded and charged <TeamsPricing />.
                </span>
              ) : (
                'lose access to Team features.'
              )}
            </span>
          </ToggleTeamsUpgradeButton>
        );
      },

      shouldRender:
        !isVBOrLegacyUrl() && Account.selectors.isTrialingTeams(currentAccount),
    },
    {
      backgroundColor: teamColors.backgroundColor,
      fontColor: teamColors.fontColor,

      /**
       * Teams account has been recently downgraded
       */
      render: () => <TeamsDowngradedBanner />,
      shouldRender: useShouldRenderTeamsDowngradedBanner({
        skip: isLoggedOut || isVBOrLegacyUrl(),
      }),
    },
    {
      backgroundColor: companyColors.backgroundColor,
      fontColor: companyColors.fontColor,

      /**
       * Companies trial is active
       */
      render: () => {
        if (!Profile.selectors.all.isAdminOf(currentAccountId, state)) {
          return null;
        }

        const daysRemaining = Account.selectors.companiesTrialDaysLeft(
          currentAccount
        );

        const isOwner = Profile.selectors.all.isOwnerOf(
          currentAccountId,
          state
        );

        const trialEndDate = Account.selectors.trialEndDate(currentAccount);

        const daysRemainingText = `${daysRemaining} ${
          daysRemaining === 1 ? 'day' : 'days'
        } remaining`;

        const hasSeenWelcomeModal = Profile.selectors.hasViewedHelp(
          COMPANY_TRIAL_WELCOME_MODAL_TRACKING_KEY,
          currentProfile
        );

        const companyPlanName = Plan.selectors.name(companyPlan);

        return (
          <CompaniesBannerWithModal
            linkText="Meet with Automation Expert"
            modal={
              <CompaniesTrialWelcomeModal
                accountId={currentAccountId}
                initialView={
                  !hasSeenWelcomeModal && isOwner ? 'companyInfo' : 'guide'
                }
                trialEndDate={trialEndDate}
              />
            }
            onClose={() =>
              hasSeenWelcomeModal
                ? undefined
                : dispatch(
                    Profile.actions.acknowledgeHelp(
                      COMPANY_TRIAL_WELCOME_MODAL_TRACKING_KEY,
                      currentProfileId
                    )
                  )
            }
            openModalEvent={{
              name: 'company-trial-guide',
              data: {
                accountId: currentAccountId,
                profileId: currentProfileId,
                isOwner,
              },
            }}
            showModalByDefault={!hasSeenWelcomeModal && currentProfileId}
          >
            You have <strong>{daysRemainingText}</strong> of your Zapier for
            Companies trial.{' '}
            <DesignSystemLink
              color="white"
              href={buildUpgradeUrl(
                currentPlan?.name || '',
                companyPlanName || '',
                currentPlan?.interval
              )}
            >
              <span style={{ textDecoration: 'underline' }}>Upgrade now.</span>
            </DesignSystemLink>
          </CompaniesBannerWithModal>
        );
      },

      shouldRender:
        !isVBOrLegacyUrl() &&
        Account.selectors.isTrialingCompanies(currentAccount),
    },
    {
      backgroundColor: companyColors.backgroundColor,
      fontColor: companyColors.fontColor,

      /**
       * Companies trial has recently ended
       */
      render: () => (
        <CompaniesBannerWithModal
          linkText="What's next?"
          modal={
            <CompaniesTrialEndedModal
              profileId={currentProfileId}
              upgradeUrl={`/app/billing/plans/upgrade/${currentPlan?.name}/${
                companyPlan ? Plan.selectors.name(companyPlan) : ''
              }?interval=${currentPlan?.interval}`}
            />
          }
          onClose={() =>
            Profile.selectors.hasViewedHelp(
              COMPANY_TRIAL_ENDED_MODAL_TRACKING_KEY,
              currentProfile
            )
              ? undefined
              : () =>
                  dispatch(
                    Profile.actions.acknowledgeHelp(
                      COMPANY_TRIAL_ENDED_MODAL_TRACKING_KEY,
                      currentProfileId
                    )
                  )
          }
          openModalEvent={{
            name: 'company-trial-ended-modal-open',
            data: {
              profileId: currentProfileId,
            },
          }}
          showModalByDefault={
            !Profile.selectors.hasViewedHelp(
              COMPANY_TRIAL_ENDED_MODAL_TRACKING_KEY,
              currentProfile
            ) && currentProfileId
          }
        >
          Your Zapier for Companies trial <strong>has ended</strong>.
        </CompaniesBannerWithModal>
      ),

      shouldRender:
        !isOnUpgradeRoute() &&
        !isVBOrLegacyUrl() &&
        !Account.selectors.isCompany(currentAccount) &&
        daysFromNow(Account.selectors.companyTrialEndDate(currentAccount)) <= 7,
    },
    {
      backgroundColor: teamColors.backgroundColor,
      fontColor: teamColors.fontColor,

      /**
       * Account is eligible for Reduced Task Team or Company promo offer.
       */
      render: () => {
        const onDismiss = () =>
          dispatch(
            Profile.actions.acknowledgeHelp(
              tracking.reducedTaskTeamCompanyPromo.TOP_BAR_BANNER_TRACKING_KEY,
              currentProfileId
            )
          );

        return <ReducedTaskTeamCompanyTopBarBanner onDismiss={onDismiss} />;
      },
      shouldRender:
        useReducedTaskTeamCompanyTopBarBanner({
          skip: isLoggedOut || isVBOrLegacyUrl() || isOnUpgradeRoute(),
        })?.shouldRender &&
        !Profile.selectors.hasViewedHelp(
          tracking.reducedTaskTeamCompanyPromo.TOP_BAR_BANNER_TRACKING_KEY,
          currentProfile
        ),
    },
    {
      /**
       * Professional trial is active
       *
       * All Professional trial components need to be below Team and Company trial components
       * in this list since users on a Team or Company trial are also technically
       * on a Professional trial, which will cause the Professional trial banner to appear
       * while on a Team or Company trial if the Professional trial components are higher
       * in this list.
       */
      render: () => (
        <FlexLayout justify={size === 'large' ? 'space-between' : 'center'}>
          <ProfessionalTrialBanner
            trialDays={Account.selectors.trialDaysLeft(currentAccount)}
          />
          <JobsCta />
        </FlexLayout>
      ),
      shouldRender:
        !isVBOrLegacyUrl() &&
        !isOnUpgradeRoute() &&
        !isOnSharedZapPOC(state) &&
        Account.selectors.isTrialing(currentAccount),
    },
    {
      /**
       * Professional trial has recently ended
       *
       * All Professional trial components need to be below Team and Company trial components
       * in this list since users on a Team or Company trial are also technically
       * on a Professional trial, which will cause the Professional trial banner to appear
       * while on a Team or Company trial if the Professional trial components are higher
       * in this list.
       */
      render: () => <ProfessionalTrialEndedBanner />,
      shouldRender: useShouldRenderProfessionalTrialEndedBanner({
        skip: isLoggedOut || isVBOrLegacyUrl(),
      }),
    },
    {
      /**
       * User is eligible for annual billing interval
       */
      render: () => <AnnualBillingNudge />,
      shouldRender: useAnnualBillingNudge({
        skip: isLoggedOut || isVBOrLegacyUrl(),
      })?.shouldRender,
    },
    {
      /**
       * User hasn't seen jobs CTA yet
       *
       * This should be last in the list so that it only appears when no
       * other banner renders.
       */
      render: () => {
        const onDismiss = () =>
          dispatch(
            Profile.actions.acknowledgeNotification(
              'jobs_cta',
              currentProfileId
            )
          );

        return (
          <DismissibleJobsCta
            onDismiss={onDismiss}
            profileId={currentProfileId}
          />
        );
      },
      shouldRender:
        !isVBOrLegacyUrl() &&
        !isOnSharedZapRoute(state) &&
        !isOnUpgradeRoute() &&
        !Profile.selectors.hasSeenJobsCta(currentProfile) &&
        !Account.selectors.isCompany(currentAccount),
    },
  ];

  const currentUrl =
    typeof window !== undefined ? window.location.pathname : undefined;

  const shouldRender =
    size !== 'small' && !isUrlBlacklisted(currentUrl, hiddenRouteMatches);

  if (!shouldRender) {
    return null;
  }

  const componentToRender = topBarComponents.find(
    component => component.shouldRender
  );
  const children = componentToRender && componentToRender.render();

  return children ? (
    <div
      css={rootStyles({
        backgroundColor: componentToRender.backgroundColor,
        fontColor: componentToRender.fontColor,
      })}
    >
      {children}
    </div>
  ) : null;
};

export default TopBar;
